import Roles, {isStaffOnlyRoleSet} from "./roles";

/**
 * Sichtbarkeitsregeln für Mitarbeitenden-Zugänge (#707). Geteilt zwischen Server und
 * Client, deshalb ohne Server-Import.
 *
 * Gespeichert in `setting.staffVisibilityRules` (jsonb, NULL = alle Defaults). Jeder
 * Default entspricht dem Verhalten vor den Regeln: ein Mandant, der nichts umstellt,
 * merkt nichts.
 */
export enum StaffVisibilityLevel {
    All = "All",
    OwnCourses = "OwnCourses",
    NoAccess = "NoAccess",
    // Nur Zeile „Personal“ (#709): fremde Datensätze nur mit Name und dienstlichen Kontaktdaten.
    ColleagueList = "ColleagueList",
}

// Weite der Stufen, eng → weit, über alle Zeilen (ColleagueList und OwnCourses kommen nie in
// derselben Zeile vor). Die Assistenz darf nie weiter sehen als die Kursleitung.
const LEVEL_ORDER: ReadonlyArray<StaffVisibilityLevel> = [
    StaffVisibilityLevel.NoAccess,
    StaffVisibilityLevel.ColleagueList,
    StaffVisibilityLevel.OwnCourses,
    StaffVisibilityLevel.All,
];

export type StaffVisibilityArea = "courses" | "customers" | "invoices" | "staff" | "locations" | "courseTypes" | "interestLists" | "bookingActions";

type AreaDefinition = {allowed: ReadonlyArray<StaffVisibilityLevel>; default: StaffVisibilityLevel};

const {All, OwnCourses, NoAccess, ColleagueList} = StaffVisibilityLevel;

// Je Zeile erlaubte Stufen + Default (Default = Verhalten vor den Regeln). „Kurse“ (#707),
// „Kunden“ (#708), übrige Zeilen #709; „courseTypes“ umfasst Kursarten UND Kategorien.
// „Buchungsaktionen“ (#1188): All = Ja, NoAccess = Nein.
export const STAFF_VISIBILITY_AREAS: Record<StaffVisibilityArea, AreaDefinition> = {
    courses: {allowed: [All, OwnCourses], default: All},
    customers: {allowed: [All, OwnCourses, NoAccess], default: All},
    invoices: {allowed: [All, OwnCourses, NoAccess], default: All},
    staff: {allowed: [All, ColleagueList, NoAccess], default: All},
    locations: {allowed: [All, NoAccess], default: All},
    courseTypes: {allowed: [All, NoAccess], default: All},
    interestLists: {allowed: [All, NoAccess], default: All},
    bookingActions: {allowed: [All, NoAccess], default: All},
};

export const STAFF_VISIBILITY_RULES_VERSION = 1;

export type StaffVisibilityRow = {instructor: StaffVisibilityLevel; assistant: StaffVisibilityLevel};
export type StaffVisibilityRules = {version: number} & Record<StaffVisibilityArea, StaffVisibilityRow>;

export const isAllowedStaffVisibilityLevel = (area: StaffVisibilityArea, level: unknown): level is StaffVisibilityLevel =>
    STAFF_VISIBILITY_AREAS[area].allowed.includes(level as StaffVisibilityLevel);

const resolveLevel = (area: StaffVisibilityArea, value: unknown): StaffVisibilityLevel =>
    isAllowedStaffVisibilityLevel(area, value) ? value : STAFF_VISIBILITY_AREAS[area].default;

/**
 * Default-Merge: unbekannte/ungültige Werte und fehlende Zeilen → Default der Zeile;
 * die Assistenz wird auf die Stufe der Kursleitung begrenzt.
 */
export const resolveStaffVisibilityRules = (raw: unknown): StaffVisibilityRules => {
    const source = (raw && typeof raw === "object" ? raw : {}) as Record<string, any>;
    const rules = {version: STAFF_VISIBILITY_RULES_VERSION} as StaffVisibilityRules;

    (Object.keys(STAFF_VISIBILITY_AREAS) as Array<StaffVisibilityArea>).forEach((area) => {
        const row = source[area] && typeof source[area] === "object" ? source[area] : {};
        const instructor = resolveLevel(area, row.instructor);
        const assistant = resolveLevel(area, row.assistant);

        rules[area] = {instructor, assistant: LEVEL_ORDER.indexOf(assistant) > LEVEL_ORDER.indexOf(instructor) ? instructor : assistant};
    });

    return rules;
};

// Rollen, mit denen ein Login als Kursleitung gilt (Spalte „instructor“), auch wenn er
// zusätzlich die Assistenz-Rolle trägt.
const INSTRUCTOR_COLUMN_ROLES: ReadonlyArray<string> = [Roles.Instructor, Roles.Staff, Roles.AreaManager, Roles.BetaTester];

/**
 * Stufe, die für den Login gilt. `null` = kein Mitarbeitenden-Zugang (Admin und höher,
 * Endkunde) → nie eingeschränkt. Reine Assistenz-Logins lesen die Spalte „assistant“,
 * alle anderen Mitarbeitenden-Zugänge die Spalte „instructor“.
 */
export const effectiveStaffVisibility = (
    rules: StaffVisibilityRules,
    area: StaffVisibilityArea,
    roles: ReadonlyArray<string> | null | undefined,
): StaffVisibilityLevel | null => {
    if (!isStaffOnlyRoleSet(roles)) {
        return null;
    }
    const isAssistantOnly = !(roles ?? []).some((role) => INSTRUCTOR_COLUMN_ROLES.includes(role));

    return isAssistantOnly ? rules[area].assistant : rules[area].instructor;
};
